import { durationToHours, roundDurationStorage } from "@/foundation/duration/durationCore";
import { getActiveDurationFormat } from "@/foundation/duration/activeFormat";

/**
 * Causali che coprono, totalmente o parzialmente, l'orario teorico giornaliero.
 * Devono quindi ridurre le ore ORDINARIE proposte dalla compilazione assistita.
 */
export const THEORETICAL_REDUCTION_CAUSES = new Set([
  "FERIE",
  "PERMESSI",
  "PERMESSI_104",
  "EX_FESTIVITA",
  "PERMESSI_STUDIO",
  "MALATTIA",
  "FESTIVITA",
  "SANTO_PATRONO",
  "CONG_PARENTALE",
  "MATERNITA",
  "ALLATTAMENTO",
]);

// M-UX-05 · parsing tramite Foundation Durate, nel formato dell'azienda attiva.
export function parseAttendanceHours(value) {
  if (value === null || value === undefined || value === "") return 0;
  const parsed = durationToHours(value, getActiveDurationFormat());
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/**
 * Riconosce esclusivamente le celle ore-causale della griglia Presenze.
 * Esclude intenzionalmente TEOR., note e altri controlli: F9 deve copiare
 * il teorico soltanto nella colonna causale scelta dall'operatore.
 */
export function parseAttendanceCauseCellTestId(testId) {
  const value = String(testId || "");
  const gridMatch = value.match(
    /^att-(\d{4}-\d{2}-\d{2})-([A-Z0-9_]+)$/,
  );
  if (gridMatch) return { date: gridMatch[1], cause: gridMatch[2] };

  const chipMatch = value.match(
    /^att-chip-input-(\d{4}-\d{2}-\d{2})-([A-Z0-9_]+)$/,
  );
  if (chipMatch) return { date: chipMatch[1], cause: chipMatch[2] };

  return null;
}

/**
 * Mantiene in equilibrio una giornata con assenze:
 * ORDINARIO = max(0, TEORICO - somma causali che riducono il teorico).
 *
 * Se non esiste alcuna assenza, non modifica ORDINARIO: questo preserva
 * eventuali ore lavorate inserite manualmente.
 */
export function rebalanceOrdinaryHours(causeHours, theoreticalHours, causeUnits) {
  const next = { ...(causeHours || {}) };
  const theoretical = Math.max(0, parseAttendanceHours(theoreticalHours));
  let absenceHours = [...THEORETICAL_REDUCTION_CAUSES].reduce(
    (total, code) => total + parseAttendanceHours(next[code]),
    0,
  );
  // Checkpoint 2 . Ferie in giorni: assenze in "gg" riducono il teorico
  // quanto qty * theoretical (1,00 gg = giornata intera; 0,50 gg = meta').
  const units = causeUnits || {};
  for (const code of THEORETICAL_REDUCTION_CAUSES) {
    const meta = units[code];
    if (meta && typeof meta === "object" && (meta.unit || "").toLowerCase() === "gg") {
      const qty = Number(meta.qty || 0);
      if (Number.isFinite(qty) && qty > 0) {
        absenceHours += qty * theoretical;
      }
    }
  }

  if (absenceHours <= 0) {
    return {
      causeHours: next,
      hasAbsence: false,
      absenceHours: 0,
      ordinaryHours: parseAttendanceHours(next.ORDINARIO),
    };
  }

  const ordinaryHours = Math.max(
    0,
    roundDurationStorage(theoretical - absenceHours, getActiveDurationFormat()),
  );
  if (ordinaryHours > 0) next.ORDINARIO = ordinaryHours;
  else delete next.ORDINARIO;

  return { causeHours: next, hasAbsence: true, absenceHours, ordinaryHours };
}

/**
 * Prepara le causali gia validate per il salvataggio senza ricalcolare
 * ORDINARIO. Il ricalcolo assistito avviene quando cambia un'assenza o
 * quando l'utente usa Ctrl+F2; al salvataggio deve invece prevalere sempre il
 * valore manuale visibile nella griglia.
 */
export function preserveManualHoursForSave(causeHours) {
  return { ...(causeHours || {}) };
}

/**
 * Una riga già salvata va inviata anche dopo la cancellazione dell'ultimo
 * valore: la mappa vuota è il comando che rimuove il dato precedente.
 */
export function shouldPersistAttendanceRow(row) {
  if (!row) return false;
  return Boolean(
    row.id
    || Object.keys(row.cause_hours || {}).length > 0
    || row.notes
    || row.theoretical_override !== undefined
  );
}
